import React, { useState } from 'react';
import { Button } from '@getro/rombo';
import { Plus } from 'lucide-react';
import { Box, Flex } from 'rebass/styled-components';
import { useSelector } from 'react-redux';
import { Redirect } from 'react-router-dom';
import toast from 'react-hot-toast';
import { AdminTable } from 'components/organisms/adminTable';
import { useNetworkAdmins } from 'hooks/useNetworkAdmins';
import { SettingsLayoutHeader } from '../../../components/atoms/settingsLayoutHeader';
import usePageHook from '../../../hooks/usePageHook';
import { activeNetworkSelector, userSelector } from '../../../redux/selectors';
import { InviteMemberModal } from './inviteMemberModal';
import { useTierGating } from '../../../hooks/useTierGating';
import AdminSupportModal from '../../../components/organisms/adminSupportModal';
import UpgradeModal from '../../../components/organisms/upgradeModal';

const pageTitle = 'Team members';

const Admins = () => {
  const {
    isLoadingNetworkAdmins,
    isLoadingResendInvite,
    isLoadingRemoveAdmin,
    isLoadingAddAdmin,
    admins,
    resendInvite,
    removeAdmin,
    hasLoadAdminsListError,
    addAdmin,
    hasAddAdminError,
  } = useNetworkAdmins();
  const activeNetwork = useSelector(activeNetworkSelector);
  const currentUser = useSelector(userSelector);
  const [isAllyModalOpen, setIsAllyModalOpen] = useState(false);
  const [isAdminInviteModalOpen, setIsAdminInviteModalOpen] = useState(false);
  const [adminSupportModalAction, setAdminSupportModalAction] = useState(null);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const { hasCapability, isCustomer: customer } = useTierGating();
  const canInviteAllies = hasCapability('invite_allies');

  usePageHook(pageTitle);

  if (!activeNetwork.isManager) {
    return (
      <Redirect
        to={{
          pathname: `/networks/${activeNetwork.slug}/settings`,
        }}
      />
    );
  }

  const handleInviteAllyClick = () => {
    if (canInviteAllies) {
      setIsAllyModalOpen(true);
    } else {
      setIsUpgradeModalOpen(true);
    }
  };

  const handleRemoveAdminClick = (admin) => {
    if (customer && admin.role !== 'ally') {
      setAdminSupportModalAction('remove_admin');
      return true;
    }
    return false;
  };

  const handleEditRoleClick = () => {
    setAdminSupportModalAction('edit_role');
    return true;
  };

  const handleInviteAdminClick = () => {
    if (customer) {
      setAdminSupportModalAction('invite_admin');
    } else {
      setIsAdminInviteModalOpen(true);
    }
  };

  return (
    <Box pr={[0, 0, '48px']}>
      <SettingsLayoutHeader
        title={pageTitle}
        description="Manage who has access to your network. For a list that also includes company admins, go to the 'Data export' page."
        actions={[
          <Flex key="invite-buttons" sx={{ justifyContent: 'flex-end', pb: '16px', pt: '8px', gap: '8px' }}>
            {customer && (
              <Button onClick={handleInviteAllyClick} variant="secondary" icon={Plus} size="small">
                Invite ally
              </Button>
            )}
            <Button onClick={handleInviteAdminClick} variant="secondary" icon={Plus} size="small">
              Invite admin
            </Button>
          </Flex>,
        ]}
        sx={{
          '& > div': {
            flexWrap: 'nowrap',
            '& >div:last-child': {
              minWidth: 'max-content',
            },
          },
        }}
      />
      <Box pt="40px" sx={{ overflowX: 'auto' }}>
        <AdminTable
          loadingAdminsError={hasLoadAdminsListError}
          isLoadingAdmins={isLoadingNetworkAdmins}
          isResendingInvitation={isLoadingResendInvite}
          isDeletingAdmin={isLoadingRemoveAdmin}
          currentUser={currentUser}
          admins={admins}
          onRemoveAdmin={removeAdmin}
          onRemoveAdminClick={handleRemoveAdminClick}
          onEditRoleClick={customer ? handleEditRoleClick : null}
          onResendInvitation={resendInvite}
        />
      </Box>
      {isAllyModalOpen && (
        <InviteMemberModal
          memberRole="ally"
          adminsEmails={admins.map((admin) => admin.email)}
          isSubmitting={isLoadingAddAdmin}
          onInvite={async (data) => {
            const { error } = await addAdmin({ ...data, role: 'ally' });
            if (error) return;
            toast('Invitation sent', {
              icon: 'success',
              id: 'invitation-sent',
            });
            setIsAllyModalOpen(false);
          }}
          setModalOpen={setIsAllyModalOpen}
          error={Boolean(hasAddAdminError)}
        />
      )}
      {isAdminInviteModalOpen && (
        <InviteMemberModal
          memberRole="admin"
          adminsEmails={admins.map((admin) => admin.email)}
          isSubmitting={isLoadingAddAdmin}
          onInvite={async (data) => {
            const { error } = await addAdmin({ ...data, role: 'network_admin' });
            if (error) return;
            toast('Invitation sent', {
              icon: 'success',
              id: 'invitation-sent',
            });
            setIsAdminInviteModalOpen(false);
          }}
          setModalOpen={setIsAdminInviteModalOpen}
          error={Boolean(hasAddAdminError)}
        />
      )}
      <AdminSupportModal
        isOpen={!!adminSupportModalAction}
        action={adminSupportModalAction}
        onClose={() => setAdminSupportModalAction(null)}
      />
      <UpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        featureName="invite_allies"
      />
    </Box>
  );
};
export default Admins;
